'use strict';

const { promises } = require('fs');
const { gql } = require('graphql-tag');
const { arrayToObject } = require('qs/lib/utils');
const rating = require('../rating');

var log = false;

var _parent = {};

class Lane {
  /**
   * Constructor for basic lane object
   * @param {Number} customer_id id of customer lane will be created for
   * @param {String} origin_name name of pickup location
   * @param {Number} origin_id id of pickup location
   * @param {String} destination_name name of deliver location
   * @param {Number} destination_id id of deliver location
   * @param {Object} mapsData object containing data from google maps API (optional)
   */

  constructor(customer_id, origin_name, origin_id, destination_name, destination_id, mapsData) {
    this.active = 1;
    this.customer_id = customer_id;
    this.description = `${origin_name} to ${destination_name}`;
    this.origin_location_id = origin_id;
    this.destination_location_id = destination_id;
    if (mapsData) {
      this.distance_miles = mapsData.miles;
      this.average_drive_speed_mph = mapsData.avgMph;
      this.duration_sec = mapsData.duration;
    }
  }
}

var lanes = {
  init: p => (_parent = p),
  /**
   * Fetches a matching lane object, returning it in an array inside of a response object.
   *
   * @param  {BigInteger} id - The ID of a lane.
   * @return  {Object} The response object containing the matching lane.
   */
  getById: id =>
    _parent.gql.query(
      gql`
        query sdk_get_lane_by_id($id: bigint!) {
          lanes(where: { id: { _eq: $id } }) {
            ...Lane
          }
        }
        ${_parent.fragments.lane()}
      `,
      { id: id }
    ),

  /**
   * Fetches all lane objects that match the entered variables, returning them in an array inside of a respone object.
   *
   * @param  {Object} [variables = null] - Variables used in where clause;
   * e.g.: {region_id: 2, active: 1} to return all active lanes for region 2.
   * A null value (default) will return all lanes the client has permissions for.
   * @return  {Object} The response object containing all matching lanes.
   */
  get: (variables = null) =>
    _parent.gql.query(
      gql`
        query sdk_get_lanes${_parent.gql.buildVariablesDeclarationString('lanes', variables)} {
          lanes${_parent.gql.buildWhereClauseString(variables)} {
            ...Lane
          }
        }
        ${_parent.fragments.lane()}
      `
    ),

  /**
   * Updates a lane object with matching ID, then returns a response object with the GQL response.
   *
   * @param  {BigInteger} id - The ID of a lane.
   * @return  {Object} The response object confirming the update.
   */
  updateById: (id, variables) =>
    _parent.gql.mutation(
      gql`
      mutation updateLaneById($id: bigint!) {
        update_lanes(
          where: {id: {_eq: $id}},
          _set: ${_parent.gql.buildUpdateClauseString(variables)}
          ) {
          affected_rows
          returning {
            ...Lane
            
          }
        }
      }   
      ${_parent.fragments.lane()}   
    `,
      { id: id }
    ),

  /**
   * Upsert lanes (for enriching lane and inverse)
   *
   * @param  {Array} laneUpsertArr - Array of lanes to upsert
   * @return  {Object} The response object confirming the update.
   */
  upsertLanes: laneUpsertArr =>
    _parent.gql.mutation(
      gql`
        mutation sdk_upsert_lanes($lanesArray: [lanes_insert_input!]!) {
          insert_lanes(
            objects: $lanesArray
            on_conflict: {
              constraint: lanes_customer_id_origin_location_id_destination_location_id_ke
              update_columns: [
                active
                distance_miles
                average_drive_speed_mph
                duration_sec
                pickup_inspection_sec
                delivery_inspection_sec
                return_ride_wait_sec
                average_drive_speed_min_per_mile
                insurance_cost_per_mile
                insurance_cost
                estimated_rideshare_return_cost
                driver_rake
                driver_time_pay
                driver_drive_pay
                driver_base_pay_discount
                driver_base_pay
                driver_return_pay_discount
                driver_return_pay
                driver_pay_per_minute
                driver_pay_per_kilometer
                driver_pay_per_mile
                dealer_base_rate
                dealer_base_rate_type
                dealer_base_price
                dealer_stranded_rate
                dealer_stranded_rate_type
                dealer_stranded_price
                dealer_base_discount
                dealer_stranded_discount
                updatedat
              ]
            }
          ) {
            affected_rows
            returning {
              ...Lane
            }
          }
        }
        ${_parent.fragments.lane()}
      `,
      { lanesArray: laneUpsertArr }
    ),

  //Lane Builder Functions
  /**
   * Wrapper for lane-builder for Dealer API
   * @param {Number} customer_id Id of the customer associated with the lane
   * @param {String} origin_name the origin location name
   * @param {String} destination_name the destination location name
   * @param {String} origin_address The address of the pickup location
   * @param {String} destination_address the address of the delivery location
   * @return {Object} The new or existing lane object
   */
  getLaneOrInsertShell: async (
    customer_id,
    origin_name,
    destination_name,
    origin_address,
    destination_address,
    options
  ) => {
    try {
      if (customer_id === undefined) {
        throw new Error(`customer id is undefined`);
      }
      if (typeof customer_id !== 'number') {
        throw new Error(`Invalid customer id: customer_id is ${typeof customer_id}`);
      }
      // Query locations or build them if they don't exist
      const locationsArr = await Promise.all([
        _parent.locations.getOrCreate(customer_id, origin_name, origin_address, null, null, null, 'customer', options),
        _parent.locations.getOrCreate(
          customer_id,
          destination_name,
          destination_address,
          null,
          null,
          null,
          'customer',
          options
        ),
      ]);
      log && console.log(locationsArr);
      //check for errors
      let errorsArr = [];
      for (var i = 0; i < 2; i++) {
        if (locationsArr[i].success === false && locationsArr[i].error) {
          console.error(locationsArr[i].error);
          errorsArr.push(_parent.errors.buildValidationError(_parent.errors.codes.ADDRESS_INVALID));
        }
      }
      if (errorsArr.length > 0) return { success: false, errors: errorsArr };
      // Get location ids
      const origin_id = locationsArr[0].data.id;
      const destination_id = locationsArr[1].data.id;

      //Lane param validation
      let laneValidation = lanes.validateLaneParams(customer_id, origin_id, destination_id);
      let laneValidationErrors = [];

      if (laneValidation && laneValidation.length > 0) {
        for (let i = 0; i < laneValidation.length; i++) {
          if (laneValidation[i].errorCode && laneValidation[i].errorCode === 'field_invalid') {
            laneValidationErrors.push(_parent.errors.buildValidationError(_parent.errors.codes.FIELD_INVALID));
          }
          if (laneValidation[i].errorCode && laneValidation[i].errorCode === 'duplicate_address') {
            laneValidationErrors.push(_parent.errors.buildValidationError(_parent.errors.codes.DUPLICATE_ADDRESS));
          }
        }
      }

      if (laneValidationErrors.length > 0) {
        return { success: false, errors: laneValidationErrors };
      }

      let res = await lanes.handleLaneShellPair(customer_id, origin_id, destination_id, origin_name, destination_name);
      if (!res) {
        throw new Error('GQL Lane upsert unexpected error.');
      }
      if (!res.success && res.message) {
        throw new Error(`GQL Lane upsert was unsuccessful: ${res.message}.`);
      }
      if (!res.laneOne || !res.laneTwo) {
        throw new Error('GQL Lane upsert did not return required lanes.');
      }

      //If lane already exists in db, it will have a distance value
      //If it doesn't have a distance value we need to create a new lane
      if (res.laneOne.distance_miles) log && console.log(`Shell Lane ${res.laneOne.id} Created`);
      else log && console.log(`Existing Lane ${res.laneOne.id} Returned`);

      // Return both lanes in an array
      return [res.laneOne, res.laneTwo];
    } catch (err) {
      console.error('getLaneOrInsertShell catch block:', err);
      //Should this be a standardized object, like the sdk's gql functions
      throw err;
    }
  },

  /**
   * Gets an existing lane or insert a "shell" lane object with all values except
   *   customer, origin, and destination ids nulled out.
   *
   * If a lane with matching customer, pickup and location exists, this will retrn it
   * If the lane does not exist, this will insert a "shell" lane containing only these
   *   three values into the db and return it
   *
   * @param {Number} customer_id Id of the customer associated with the lane
   * @param {Number} origin_id The origin_location_id of the lane
   * @param {Number} destination_id the destination_location_id of the lane
   * @param {String} description "{pickup location name} to {delivery location name}"
   * @return {Object} The new or existing lane object
   */
  laneShellUpsert: (customer_id, origin_id, destination_id, description) =>
    _parent.gql.mutation(
      gql`
        mutation sdk_insert_or_get_lane(
          $customer_id: bigint!
          $origin_id: bigint!
          $destination_id: bigint!
          $description: String!
        ) {
          insert_lanes(
            objects: {
              active: 0
              customer_id: $customer_id
              origin_location_id: $origin_id
              destination_location_id: $destination_id
              description: $description
            }
            on_conflict: {
              constraint: lanes_customer_id_origin_location_id_destination_location_id_ke
              update_columns: [updatedat, description]
            }
          ) {
            affected_rows
            returning {
              ...Lane
            }
          }
        }
        ${_parent.fragments.lane()}
      `,
      { customer_id: customer_id, origin_id, destination_id: destination_id, description: description }
    ),

  /**
   * Gets pair of existing lanes or inserts "shell" lane objects with all values except
   *   customer, origin, and destination ids nulled out.
   *
   * If a lane with matching customer, pickup and location exists, this will retrn it
   * If the lane does not exist, this will insert a "shell" lane containing only these
   *   three values into the db and return it
   *
   * @param {Number} customer_id Id of the customer associated with the lane
   * @param {Number} origin_id The origin_location_id of the lane
   * @param {Number} destination_id the destination_location_id of the lane
   * @param {String} descriptionOne "{pickup location name} to {delivery location name}"
   * @param {String} descriptionTwo "{delivery location name} to {pickup location name}"
   * @return {Object} Response object containing the new or existing lane objects
   */
  laneShellPairUpsert: (customer_id, origin_id, destination_id, descriptionOne, descriptionTwo) =>
    _parent.gql.mutation(
      gql`
        mutation sdk_insert_or_get_lane_pair(
          $customer_id: bigint!
          $origin_id: bigint!
          $destination_id: bigint!
          $descriptionOne: String!
          $descriptionTwo: String!
        ) {
          insert_lanes(
            objects: [
              {
                active: 0
                customer_id: $customer_id
                origin_location_id: $origin_id
                destination_location_id: $destination_id
                description: $descriptionOne
              }
              {
                active: 0
                customer_id: $customer_id
                origin_location_id: $destination_id
                destination_location_id: $origin_id
                description: $descriptionTwo
              }
            ]
            on_conflict: {
              constraint: lanes_customer_id_origin_location_id_destination_location_id_ke
              update_columns: [updatedat, description]
            }
          ) {
            affected_rows
            returning {
              ...Lane
            }
          }
        }
        ${_parent.fragments.lane()}
      `,
      {
        customer_id: customer_id,
        origin_id,
        destination_id: destination_id,
        descriptionOne: descriptionOne,
        descriptionTwo: descriptionTwo,
      }
    ),

  /**
   * Gets pair of existing lanes or inserts "shell" lane objects with all values except
   *   customer, origin, and destination ids nulled out.
   *
   * If a lane with matching customer, pickup and location exists, this will retrn it
   * If the lane does not exist, this will insert a "shell" lane containing only these
   *   three values into the db and return it
   *
   * @param {Number} customer_id Id of the customer associated with the lane
   * @param {Number} origin_id The origin_location_id of the lane
   * @param {Number} destination_id the destination_location_id of the lane
   * @param {String} origin_name name of pickup location
   * @param {String} destination_name name of destination location
   * @return {Object} Object containing primary and inverse lane, labeled
   */
  handleLaneShellPair: async (customer_id, origin_id, destination_id, origin_name, destination_name) => {
    const descriptionOne = origin_name + ' to ' + destination_name;
    const descriptionTwo = destination_name + ' to ' + origin_name;
    const lanePairRes = await _parent.lanes.laneShellPairUpsert(
      customer_id,
      origin_id,
      destination_id,
      descriptionOne,
      descriptionTwo
    );
    log && console.log('lanePairRes', lanePairRes);
    if (!lanePairRes || lanePairRes.success === false) {
      const error =
        lanePairRes.errors && Array.isArray(lanePairRes.errors) && lanePairRes.errors[0]
          ? lanePairRes.errors[0]
          : 'unknown error';
      console.error('Lane pair failed to upsert: ', error);
      return { success: false, message: 'Lane pair upsert failed', laneOne: null, laneTwo: null };
    }
    if (lanePairRes && Array.isArray(lanePairRes.data) && lanePairRes.data.length < 2) {
      console.error('Lane pair upsert did not return two lanes');
      return { success: false, message: 'Lane pair upsert did not return two lanes', laneOne: null, laneTwo: null };
    }
    if (lanePairRes.success && Array.isArray(lanePairRes.data) && lanePairRes.data.length === 2) {
      log && console.log('Lane pair upserted successfully');
      const laneOne = lanePairRes.data.find(lane => lane.origin_location_id === origin_id);
      const laneTwo = lanePairRes.data.find(lane => lane.destination_location_id === origin_id);
      return { success: true, message: 'lane pair upserted successfully', laneOne: laneOne, laneTwo: laneTwo };
    }
    console.error('Unexpected lane pair upsert response');
    return { success: false, message: 'unexpected response', laneOne: null, laneTwo: null };
  },

  /**
   * @param  {Number} customer_id unique identifier representing the customer lane will be created for/by
   * @param  {Object} origin - An object containing id, name, and address of the pickup location.
   * @param  {Number} origin.id - Pickup location id- only present for existing location. (optional)
   * @param  {String} origin.name - Pickup location name.
   * @param  {String} origin.address - Pickup location address.
   * @param  {Object} destination - An object containing id, name, and address of the deliver location.
   * @param  {Number} destination.id - Delivery location id- only present for existing location. (optional)
   * @param  {String} destination.name - Delivery location name.
   * @param  {String} destination.address - Delivery location address.
   * @returns {Array} Contains all new lane objects. If lane and its inverse already exist, returns an Error
   */
  build: async (customerId, origin, destination) => {
    try {
      if (!customerId || typeof customerId !== 'number') throw new Error('Invalid customer id');
      if (!origin || typeof origin !== 'object') throw new Error('Invalid pickup location');
      if (!destination || typeof destination !== 'object') throw new Error('Invalid destination location');
      if (origin.id && destination.id) {
        return lanes.buildWithDetails(customerId, origin, destination);
      } else {
        return lanes.buildFromAddresses(customerId, origin.name, origin.address, destination.name, destination.address);
      }
    } catch (err) {
      throw err;
    }
  },

  /**
   * Returns array of new Lane Objects with details calculated
   *
   * @param  {Number} customer_id unique identifier representing the customer lane will be created for/by
   * @param  {Object} originLocation - An object containing id, name, and address of the pickup location.
   * @param  {Number} originLocation.id - Pickup location id- only present for existing location. (optional)
   * @param  {String} originLocation.name - Pickup location name.
   * @param  {String} originLocation.address - Pickup location address.
   * @param  {Object} destinationLocation - An object containing id, name, and address of the deliver location.
   * @param  {Number} destinationLocation.id - Delivery location id- only present for existing location. (optional)
   * @param  {String} destinationLocation.name - Delivery location name.
   * @param  {String} destinationLocation.address - Delivery location address.
   * @returns {Array} Contains all new lane objects. If lane and its inverse already exist, throws an Error
   *
   */
  buildWithDetails: async (customer_id, originLocation, destinationLocation) => {
    //Build basic lane object
    let newLaneArray = await _parent.lanes.buildFromLocations(
      customer_id,
      originLocation.name,
      originLocation.address,
      originLocation.id,
      destinationLocation.name,
      destinationLocation.address,
      destinationLocation.id
    );
    if (newLaneArray.length > 0 && newLaneArray !== Error && !newLaneArray.includes(null)) {
      // sets each lanes details to defaults
      let detailedLaneArray = [];

      const rideshareRatesRes = await _parent.lanes.getRideshareRates(customer_id);
      let rideshareRate = rideshareRatesRes && rideshareRatesRes.data ? rideshareRatesRes.data : null;

      //TODO: replace this for loop with a promise all to speed things up
      //Rob 10/21 - Followed through with the above TODO from Ian. Replaced code
      // commented out below for reference. Tests showed this cut runtime in half.
      await Promise.all(
        newLaneArray.map(async newLane => detailedLaneArray.push(await _parent.lanes.calculateDetails(newLane, rideshareRate)))
      );

      // for (var i = 0; i < newLaneArray.length; i++) {
      //   console.time(`lanes.calculateDetails(newLaneArray[${i}])`)
      //   log && console.log('calculateDetails with array: ', newLaneArray[i]);
      //   let detailedLane = await _parent.lanes.calculateDetails(newLaneArray[i]);
      //   detailedLaneArray.push(detailedLane);
      //   console.timeEnd(`lanes.calculateDetails(newLaneArray[${i}])`)
      // }

      log && console.log(`${detailedLaneArray.length} Lanes detailed`);
      return detailedLaneArray;
    } else {
      log && console.log('Lanes already exist');
      throw new Error('Lanes already exist');
    }
  },

  /**
   * Builds Lanes from two existing Locations (if the Lanes don't already exist)
   *
   * @param  {Number} customer_id unique identifier representing the customer lane will be created for/by
   * @param {String} origin_name name of pickup location
   * @param {String} origin_address address of pickup location
   * @param {Number} origin_id id of pickup location
   * @param {String} destination_name name of deliver location
   * @param {String} destination_address address of deliver location
   * @param {Number} destination_id id of deliver location
   * @returns {Array} Contains all new lane objects. If lane and its inverse already exist, returns an Error
   *
   */

  async buildFromLocations(
    customer_id,
    origin_name,
    origin_address,
    origin_id,
    destination_name,
    destination_address,
    destination_id
  ) {
    try {
      // checks if existing locations already have full lanes
      //NOTE: added the promise.all() to save some time
      let lanesExist = await Promise.all([
        _parent.lanes.get({
          customer_id: customer_id,
          origin_location_id: origin_id,
          destination_location_id: destination_id,
        }),
        _parent.lanes.get({
          customer_id: customer_id,
          origin_location_id: destination_id,
          destination_location_id: origin_id,
        }),
      ]);
      //Checks that lanes exist and that they are not shells (i.e. they have distance data)
      let laneExists =
        lanesExist[0].success && lanesExist[0].data.length > 0 && lanesExist[0].data[0].distance_miles !== null;
      let inverseExists =
        lanesExist[1].success && lanesExist[1].data.length > 0 && lanesExist[1].data[0].distance_miles !== null;

      // If both lanes found, no need to create any lanes
      if (laneExists && inverseExists) {
        let errmess = 'Two lanes already exist for these locations';
        console.error(errmess);
        throw Error(errmess);
      }
      // One call because we use the same maps data for both lanes
      let mapsDataObj = await _parent.google.returnLaneData(origin_address, destination_address);
      let newPrimaryLane = {};
      let newInverseLane = {};
      if (!laneExists && !inverseExists) {
        newPrimaryLane = new Lane(customer_id, origin_name, origin_id, destination_name, destination_id, mapsDataObj);
        newInverseLane = new Lane(customer_id, destination_name, destination_id, origin_name, origin_id, mapsDataObj);
        let newLaneArray = [newPrimaryLane, newInverseLane];
        if (log) {
          log && console.log(`created 2 Lanes: ${newLaneArray[0].description}, ${newLaneArray[1].description}`);
        }
        return newLaneArray;
        // Lanes should always be created in pairs, but these next two case are included as fail-safes
      } else if (!laneExists && inverseExists) {
        newPrimaryLane = new Lane(customer_id, origin_name, origin_id, destination_name, destination_id, mapsDataObj);
        let newLaneArray = [newPrimaryLane];
        if (log) {
          log && console.log(`created primary Lane: ${newLaneArray[0].description}`);
        }
        return newLaneArray;
      } else if (laneExists && !inverseExists) {
        newInverseLane = new Lane(customer_id, destination_name, destination_id, origin_name, origin_id, mapsDataObj);
        let newLaneArray = [newInverseLane];
        if (log) {
          log && console.log(`created inverse Lane: ${newLaneArray[0].description}`);
        }
        return newLaneArray;
      }
    } catch (err) {
      throw err;
    }
  },

  //TODO: deconstruct lane param object into separate int and string params
  /**
   * Calculate all pricing and driver pay fields for the lane object
   * and populate those fields Into the passed in lane object
   *
   * @param  {Object} lane - The lane object to update REQ
   * @param  {Number} perMinuteRate - The pay per minute rate in dollars OPT - defaults to a preset
   * @param  {Number} perMileRate - The pay per mile rate in dollars OPT - defaults to a preset
   * @param  {Number} dealerBaseDiscount - The dollars to apply as a discount to the base price OPT - default to 0.00
   * @param  {Number} dealerStrandedDiscount - The dollars to apply as a discount to the stranded price OPT - default to 0.00
   * @param  {Number} basePayDiscount - The dollars to apply as a discount to the base pay OPT - default to 0.00
   * @param  {Number} returnPayDiscount - The dollars to apply as a discount to the return pay OPT - default to 0.00
   * @param  {Number} basePayRake - The percentage of driver pay to keep as company rake (DEPRECATED) OPT - default to 0.00 ?
   * @param  {Number} tolls - The dollars to add to the pay for expected tolls on route OPT - default to 0.00
   * @return  {Object} - returns modified Lane Object
   */
  async calculateDetails(
    lane,
    rideshareRate = null,
    perMinuteRate = null,
    perMileRate = null,
    dealerBaseDiscount = 0,
    dealerStrandedDiscount = 0,
    basePayDiscount = 0,
    returnPayDiscount = 0,
    basePayRake = 0,
    tolls = 0
  ) {
    try {
      //clone lane object to be returned if function fails
      let laneClone = { ...lane };

      let timer = Date.now();
      //The lane distance data from which all other data is ultimately calculated
      let miles = lane.distance_miles;
      let avgMph = lane.average_drive_speed_mph;
      log && console.log('LANE', lane);

      //Calculate timing data
      const laneTimingObj = lanes.calculateLaneTiming(miles, avgMph);
      // const {
      //   duration_sec,
      //   pickup_inspection_sec,
      //   delivery_inspection_sec,
      //   return_ride_wait_sec,
      //   average_drive_speed_min_per_mile,
      // } = laneTimingObj;

      //Assign time, speed, and duration values to lane
      Object.assign(lane, laneTimingObj);

      //calculate third party costs (tolls and estimated rideshare cost)
      const thirdPartyCosts = lanes.calculateThirdPartyCosts(miles, avgMph, tolls, rideshareRate);
      log && console.log(`Calculated basic details for lane ${lane.description}`, Date.now() - timer);

      //Assign insurance and rideshare costs to lane
      Object.assign(lane, thirdPartyCosts);

      //TODO: Calculate driver and dealer amounts at the same time in a Promise.all
      let laneWithDriverPay = await lanes.calculateDriverDetails(
        lane,
        perMinuteRate,
        perMileRate,
        basePayDiscount,
        returnPayDiscount,
        basePayRake,
        tolls
      );
      log && console.log(`Calculated Drive details for lane ${lane.description}`, Date.now() - timer);

      //DRIVER PAY DETAILS
      // lane.driver_rake
      // lane.driver_base_pay
      // lane.driver_return_pay_discount
      // lane.driver_return_pay
      // lane.driver_pay_per_minute
      // lane.driver_pay_per_kilometer
      // lane.driver_pay_per_mile

      //Assign driver pay to lane
      Object.assign(lane, laneWithDriverPay);

      //02/14/2025 NOTE: This function is severely deprecated and will be deleted in the future (during lane builder rewrite)
      let laneWithDealerCosts = await lanes.calculateDealerDetails(
        lane,
        dealerBaseDiscount,
        dealerStrandedDiscount,
        tolls
      );
      log && console.log(`Calculated Dealer details for lane ${lane.description}`, Date.now() - timer);

      //Assign dealer costs to lane
      Object.assign(lane, laneWithDealerCosts);

      log && console.log(`Completed lane:`, lane);

      //01/12/21 Hotfix: This function was returning lane objects fully nulled out except for the Dealer details
      //Make sure "lane" is a lane object and not something else- if it is, just return the lane argument (will contain basic lane info)
      if (lane.customer_id && lane.origin_location_id && lane.destination_location_id) {
        return lane;
      } else {
        //Return cloned lane param so at least basic lane values (just as distance and description) can be inserted
        return laneClone;
      }
    } catch (err) {
      throw err;
    }
  },

  /**
   * Calculate timing for lane
   *
   * @param  {Number} miles - The lane's distance_miles prop (from google) REQ
   * @param  {Number} average_drive_speed_mph - The lane's average_drive_speed_mph prop estimated average speed for the lane (from google) REQ
   * @return  {Object} - returns object containing duration, inspections, return ride wait and average drivespeed per min
   */
  calculateLaneTiming(miles, avgMph) {
    // param validation?
    var laneTimingObj = {
      //Lane drive and wait times
      duration_sec: _parent.rating.core.getTotalDuration(miles, avgMph),
      pickup_inspection_sec: _parent.rating.core.getPickupInspectionTimeAsSeconds(),
      delivery_inspection_sec: _parent.rating.core.getDeliveryInspectionTimeAsSeconds(),
      return_ride_wait_sec: _parent.rating.core.getReturnRideWaitTimeAsSeconds(),
      average_drive_speed_min_per_mile: _parent.rating.core.getMilesPerMinute(avgMph),
    };

    log && console.log('basic lane details', laneTimingObj);

    return laneTimingObj;
  },

  /**
   * Calculate insurance costs and estimated return_ride costs
   *
   * @param  {Number} miles - The lane's distance_miles prop (from google) REQ
   * @param  {Number} average_drive_speed_mph - The lane's average_drive_speed_mph prop estimated average speed for the lane (from google) REQ
   * @param  {Number} tolls - The dollars to add to the pay for expected tolls on route OPT - default to 0.00
   * @return  {Object} - returns object containing tolls and estimated rideshare cost
   */
  calculateThirdPartyCosts(miles, avgMph, tolls = 0, rideshareRate = null) {
    // param validation?
    var thirdPartyCosts = {
      //Third party costs
      tolls: tolls,
      //Insurance costs moved to dealer price section of lane builder
      estimated_rideshare_return_cost: _parent.rating.thirdParty.getEstimatedReturnRideCost(miles, avgMph, tolls, rideshareRate),
    };

    log && console.log('basic lane details', thirdPartyCosts);

    return thirdPartyCosts;
  },

  /**
   * Calculate all driver pay fields for the lane object
   * and populate those fields Into the passed in lane object
   *
   * @param  {Object} lane - The lane object to update REQ
   * @param  {Number} perMinuteRateOverride - The pay per minute rate in dollars OPT - defaults to a preset
   * @param  {Number} perMileRateOverride - The pay per mile rate in dollars OPT - defaults to a preset
   * @param  {Number} basePayDiscount - The dollars to apply as a discount to the base pay OPT - default to 0.00
   * @param  {Number} returnPayDiscount - The dollars to apply as a discount to the return pay OPT - default to 0.00
   * @param  {Number} basePayRake - The percentage of driver pay to keep as company rake (DEPRECATED) OPT - default to 0.00 ?
   * @param  {Number} tolls - The dollars to add to the pay for expected tolls on route OPT - default to 0.00
   * @return  {Object} - returns modified Lane Object
   */
  async calculateDriverDetails(
    lane = {
      distance_miles: 0,
      average_drive_speed_min_per_mile: 0,
      duration_sec: 0,
    },
    perMinuteRateOverride = null,
    perMileRateOverride = null,
    basePayDiscount = 0,
    returnPayDiscount = 0,
    basePayRake = 0,
    tolls = 0
  ) {
    try {
      //TODO: param validation
      let { distance_miles, average_drive_speed_min_per_mile, duration_sec, customer_id } = lane;

      //Set all default values to zero, to prevent lane builder from breaking
      //  (also alphabetizes lane properties, which helps wih testing)
      lane.driver_base_pay = 0;
      lane.driver_base_pay_discount = 0;
      lane.driver_drive_pay = 0;
      lane.driver_pay_per_kilometer = 0;
      lane.driver_pay_per_mile = 0;
      lane.driver_pay_per_minute = 0;
      lane.driver_rake = 0;
      lane.driver_return_pay = 0;
      lane.driver_return_pay_discount = 0;
      lane.driver_time_pay = 0;

      //Driver pay functions take move as an input, not lane. So we create a "move" object here to pass in
      //These are the values PayRates uses to query driver pay
      //      date: move.pickup_time, (here we replace this with current time)
      //      customer: move.customer_id,
      //      distance: move.lane.distance_miles,
      //      region: region, (this can be found via the lane's origin_location_id)
      let moveObj = {
        pickup_time: new Date().toISOString(),
        customer_id: customer_id,
        lane: lane,
      };

      //do this before calling the driverpay functions to reduce the number of queries
      if (perMinuteRateOverride === null || perMileRateOverride === null) {
        log && console.log('**NO OVERRIDES**');
        let payRates = await rating.driverPay(_parent.config.server, _parent.config.secret).getDriverRate(moveObj);
        //01/12/21 Hotfix: This function was returning lane objects fully nulled out except for the Dealer details
        //Added ternary to prevent Type Error from breaking lane creation
        var perMinuteRate = perMinuteRateOverride || payRates ? payRates.per_minute_rate : null;
        var perMileRate = perMileRateOverride || payRates ? payRates.per_mile_rate : null;
      } else {
        var perMinuteRate = perMinuteRateOverride;
        var perMileRate = perMileRateOverride;
      }
      log && console.log('Before calling driverPay:', moveObj);

      //Appy Driver Pay discounts (not currently used in our system)
      lane.driver_base_pay_discount = basePayDiscount;
      lane.driver_return_pay_discount = returnPayDiscount;

      // driver_rake is not currently used, but can still be set if needed
      lane.driver_rake = basePayRake;

      //Calculate two types of drive pay: time and drive
      //These values are assigned to the lane and used when calculating base and return pay
      let driverTimePay = await rating
        .driverPay(_parent.config.server, _parent.config.secret)
        .getDriverBaseTimePay(duration_sec, perMinuteRate, moveObj);
      log && console.log('lane.driver_time_pay:', driverTimePay);
      lane.driver_time_pay = driverTimePay;

      let driverDrivePay = await rating
        .driverPay(_parent.config.server, _parent.config.secret)
        .getDriverBaseDrivePay(distance_miles, perMileRate, moveObj);
      log && console.log('lane.driver_drive_pay:', driverDrivePay);
      lane.driver_drive_pay = driverDrivePay;

      //Calculate driver base pay
      let driverBasePay = await rating
        .driverPay(_parent.config.server, _parent.config.secret)
        .getDriverBasePay(moveObj, perMinuteRate, perMileRate);
      log && console.log('lane.driver_base_pay:', driverBasePay);

      //Calculate Driver Return Ride Pay
      let driverReturnPay = await rating
        .driverPay(_parent.config.server, _parent.config.secret)
        .getDriverReturnPay(moveObj, perMinuteRate);
      log && console.log('lane.driver_return_pay:', driverReturnPay);

      //Apply driver pay fields to the lane obj
      lane.driver_base_pay = driverBasePay;
      lane.driver_pay_per_kilometer = perMileRate * 1.60934; // Multiplying miles by 1.60934 gets kilometers
      lane.driver_pay_per_mile = perMileRate;
      lane.driver_pay_per_minute = perMinuteRate;
      lane.driver_return_pay = driverReturnPay;

      log && console.log('lane with driver pay:', lane);
      return lane;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  /**
   * Calculate pricing fields for the lane object
   * and populate those fields Into the passed in lane object
   * Note: If a non-fatal exception occurs, default values will be returned
   *
   * @param  {Object} lane - The lane object to update REQ
   * @param  {Number} lane.customer_id - The customer of the lane is neccessary to select the correct rate
   * @param  {Number} lane.distance_miles - The length of the lane in miles is needed to determine the rate to apply
   * @param  {Number} baseDiscountOverride - The dollars to apply as a discount to the base price OPT
   * @param  {Number} strandedDiscountOverride - The dollars to apply as a discount to the stranded price OPT
   * @param  {Number} tolls - The dollars to add to the pay for expected tolls on route OPT - default to 0.00
   * @return  {Object} - returns modified Lane Object
   */
  async calculateDealerDetails(lane, baseDiscountOverride = null, strandedDiscountOverride = null, tolls = 0) {
    //02/14/2025 NOTE: This function is severely deprecated and will be deleted in the future (during lane builder rewrite)
    try {
      //Param validation
      const { distance_miles, customer_id, dealer_base_discount, dealer_stranded_discount } = lane;
      if (!distance_miles)
        throw new Error(`Failed to Calculate Dealer Rates: Lane has distance_miles value of ${distance_miles}`);
      if (!customer_id) throw new Error('Failed to Calculate Dealer Rates: Lane is missing customer_id');
      if (isNaN(distance_miles) || isNaN(customer_id) || isNaN(tolls)) {
        throw new Error(
          `Required param value is not a number. Please check the values and try again: distance_miles:  ${distance_miles}, customer_id: ${customer_id}, tolls: ${tolls}`
        );
      }

      //Fetch customer's raterules
      //TODO: separate this fetch into its own function
      // const GQL_MILEAGEBANDS = gql`
      //   query fetchMileageBandsLaneBuilder($customer: bigint!) {
      //     raterules(where: { active: { _eq: "1" }, customer_id: { _eq: $customer } }) {
      //       distance_start
      //       distance_end
      //       rate
      //       type
      //       class
      //     }
      //     insurancerates(
      //       where: {
      //         type: { _ilike: "per mile" }
      //         raterulegroups: {
      //           customer_id: { _eq: $customer }
      //           begin_date: { _lte: "now()" }
      //           end_date: { _gte: "now()" }
      //         }
      //       }
      //     ) {
      //       id
      //       rate
      //     }
      //   }
      // `;

      // NOTE: assigning rate data to a lane object is deprecated. Commenting this out so all lanes will be created with default values
      // const rateRulesRes = await _parent.gql.query(GQL_MILEAGEBANDS, { customer: customer_id });

      // log && console.log('rateRulesRes', rateRulesRes);

      //If getting milage bands fails, default values will be returned (zeros and 'flat')
      //Default all rate fields
      lane.dealer_base_rate = 0.0;
      lane.dealer_base_rate_type = 'flat';
      lane.dealer_base_price = 0.0;
      lane.dealer_stranded_rate = 0.0;
      lane.dealer_stranded_rate_type = 'flat';
      lane.dealer_stranded_price = 0.0;
      lane.insurance_cost_per_mile = 0.0;
      lane.insurance_cost = 0.0;

      // let ratingsArray =
      //   rateRulesRes.data &&
      //   rateRulesRes.data.raterules &&
      //   Array.isArray(rateRulesRes.data.raterules)
      //     ? rateRulesRes.data.raterules
      //     : [];

      // log && console.log("ratingsArray", ratingsArray)

      // //Get base and stranded mileage bands for the lane mileage
      // let baseBand =
      //   ratingsArray && ratingsArray.length > 0
      //     ? _parent.rating.pricing.getMileageBand(distance_miles, 'base', ratingsArray)
      //     : null;
      // let strandedBand =
      //   ratingsArray && ratingsArray.length > 0
      //     ? _parent.rating.pricing.getMileageBand(distance_miles, 'stranded', ratingsArray)
      //     : null;

      // //Discounts
      // lane.dealer_base_discount = this.determineDiscountValue(dealer_base_discount, baseDiscountOverride);
      // lane.dealer_stranded_discount = this.determineDiscountValue(dealer_stranded_discount, strandedDiscountOverride);

      // //Populate the base rate, type, and price onto the lane object
      // if (baseBand !== null && baseBand !== undefined) {
      //   lane.dealer_base_rate = baseBand.rate;
      //   lane.dealer_base_rate_type = baseBand.type;
      //   lane.dealer_base_price = _parent.rating.pricing.getCustomerPrice(
      //     distance_miles,
      //     baseBand.flat_rate,
      //     baseBand.rate,
      //     baseBand.type,
      //     lane.dealer_base_discount,
      //     lane.tolls
      //   );
      // }

      // //Populate the stranded rate, type, and price onto the lane object
      // if (strandedBand !== null && strandedBand !== undefined) {
      //   lane.dealer_stranded_rate = strandedBand.rate;
      //   lane.dealer_stranded_rate_type = strandedBand.type;
      //   lane.dealer_stranded_price = _parent.rating.pricing.getCustomerPrice(
      //     distance_miles,
      //     strandedBand.flat_rate,
      //     strandedBand.rate,
      //     strandedBand.type,
      //     lane.dealer_stranded_discount,
      //     lane.tolls
      //   );
      // }
      
      // //Insurance Rate (fetched along with the milagebands)
      // let insuranceRate =
      //   rateRulesRes.data && Array.isArray(rateRulesRes.data.insurancerates) && rateRulesRes.data.insurancerates.length
      //     ? rateRulesRes.data.insurancerates[0].rate
      //     : 0;
      // let insuranceCost = Number((distance_miles * insuranceRate).toFixed(2));
      // // let insuranceCost = _parent.rating.thirdParty.getPerMileInsuranceCost(miles, insuranceRate);

      // log && console.log("insuranceRate", insuranceRate)
      
      // lane.insurance_cost_per_mile = insuranceRate;
      // lane.insurance_cost = insuranceCost;

      // log && console.log('returning lane:', lane);
      return lane;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  /**
   * Function that determines dealer discount values in calculateDealerDetails()
   *
   * @param  {Number} currentDiscount - the discount value currently on the lane
   * @param  {Number} newDiscount - discount value entered as an argument in calculateDealerDetails
   * @returns {Number} the discount value to set on the lane
   */
  determineDiscountValue: (currentDiscount, newDiscount) => {
    //Note: I used typeof ... "number" instead of !== undefined/null in the off chance a discount is entered as a string/object
    log && console.log('discounts', currentDiscount, newDiscount);
    if (typeof currentDiscount === 'number' && newDiscount === null) {
      //No override- keep original value
      return currentDiscount;
    } else if (typeof currentDiscount !== 'number' && typeof newDiscount === 'number') {
      //No current value and override is entered - set override
      return newDiscount;
    } else if (typeof currentDiscount === 'number' && typeof newDiscount === 'number') {
      //Current value exists but override has been entered - set override
      return newDiscount;
    } else {
      //Default outcome; either neither value is defined (new lane)
      //  or there was an unexpected outcome and we want to avoid breaking lane builder
      return 0;
    }
  },

  //BUILD FROM ADDRESSES FUNCTIONS (NOT USED)

  /**
   * Function used to create a new lane from two addresses
   *
   * @param  {Number} customer_id - customer the lane will be created for. REQUIRED
   * @param {String} origin_name - Starting location's name. OPTIONAL
   * @param {String} origin_address - Starting location's address. Formatted <1234 Some Name Street (or abbr.), City, State (or state code) 12345> REQUIRED
   * @param {String} destination_name - Ending location's name. OPTIONAL
   * @param {String} destination_address - Ending location's address. Formatted <1234 Some Name Street (or abbr.), City, State (or state code) 12345> REQUIRED
   * @returns {Object} Lane objects for review and detailing
   */

  async buildFromAddresses(customer_id, origin_name, origin_address, destination_name, destination_address) {
    try {
      //Param validation
      if (!customer_id || typeof customer_id !== 'number') throw new Error('Invalid customer_id');
      if (!origin_address || typeof origin_address !== 'string')
        throw new Error(`Invalid origin_address: ${origin_address}`);
      if (!destination_address || typeof destination_address !== 'string')
        throw new Error(`Invalid destination_address: ${destination_address}`);
      if (!origin_name || typeof origin_name !== 'string')
        origin_name = _parent.locations.defaultLocationName(origin_address);
      if (!destination_name || typeof destination_name !== 'string')
        destination_name = _parent.locations.defaultLocationName(destination_address);
      //use Google Maps data for distance and avg mph of lanes
      var mapsData = await _parent.google.returnLaneData(origin_address, destination_address);
      //  Get locations or build them if new
      let originObj = await _parent.locations.getOrBuild(customer_id, origin_name, origin_address);
      let destinationObj = await _parent.locations.getOrBuild(customer_id, destination_name, destination_address);
      // Build new lanes from two existing locations
      if (originObj.id && destinationObj.id) {
        if (log) {
          log &&
            console.log(
              `Two old locations: origin = ${JSON.stringify(originObj)},\n destination = ${JSON.stringify(
                destinationObj
              )}`
            );
        }
        return lanes.buildWithDetails(customer_id, originObj, destinationObj);
      }
      // Build new Lanes with new location
      else {
        // New origin Location, Existent destination Location
        if (!originObj.id && destinationObj.id) {
          if (log) {
            log &&
              console.log(
                `New location: origin = ${JSON.stringify(originObj)},\n Old location:destination = ${JSON.stringify(
                  destinationObj
                )}`
              );
          }
          let laneWithNested = await lanes.buildNestedLane(customer_id, originObj, destinationObj, mapsData);
          let inverseLaneWithNested = await lanes.buildNestedLane(customer_id, destinationObj, originObj, mapsData);
          return [laneWithNested, inverseLaneWithNested];
        }
        // Existent origin Location, New destination Location
        else if (originObj.id && !destinationObj.id) {
          if (log) {
            log &&
              console.log(
                `Old location: origin = ${JSON.stringify(originObj)},\n New location:destination = ${JSON.stringify(
                  destinationObj
                )}`
              );
          }
          let laneWithNested = await lanes.buildNestedLane(customer_id, originObj, destinationObj, mapsData);
          let inverseLaneWithNested = await lanes.buildNestedLane(customer_id, destinationObj, originObj, mapsData);
          return [laneWithNested, inverseLaneWithNested];
        }
        // If both locations are new, then 2 locations will be created and an array of two lanes will be returned
        else if (!originObj.id && !destinationObj.id) {
          // LaneCreation requires Locations with IDs which have been pulled from db to avoid locations only existing in lane
          if (log) {
            log &&
              console.log(
                `Two new locations: origin = ${JSON.stringify(originObj)},\n destination = ${JSON.stringify(
                  destinationObj
                )}`
              );
          }
          let laneWithNested = await lanes.buildNestedLane(customer_id, originObj, destinationObj, mapsData);
          let inverseLaneWithNested = await lanes.buildNestedLane(customer_id, destinationObj, originObj, mapsData);
          return [laneWithNested, inverseLaneWithNested];
        }
      }
    } catch (err) {
      throw err;
    }
  },

  /**
   * Returns Lane Objects with nested location(s)
   *
   * @param {Object} Lane - lane object to nest locations within
   * @param  {Number} customer_id - customer the lane will be created for.
   * @param {Object} Origin - location containing name, address, and either id or lat and long
   * @param {Object} Destination - location containing name, address, and either id or lat and long
   * @returns {Object} Lane object with nested location object(s)
   *
   */

  assignNestedLocations(Lane, customer_id, Origin, Destination) {
    // validation for all object values will go here
    if (Origin.id) {
      var pickupData = { origin_location_id: Origin.id };
    } else {
      var pickupData = {
        pickup: {
          data: {
            name: Origin.name,
            address: Origin.address,
            latitude: Origin.latitude,
            longitude: Origin.longitude,
            active: 1,
            customer_id: customer_id,
            region_id: Origin.region_id,
            updatedat: 'now()',
          },
          on_conflict: {
            constraint: 'locations_customer_id_address_key',
            update_columns: ['active'],
          },
        },
      };
    }
    if (Destination.id) {
      var deliveryData = { destination_location_id: Destination.id };
    } else {
      var deliveryData = {
        delivery: {
          data: {
            name: Destination.name,
            address: Destination.address,
            latitude: Destination.latitude,
            longitude: Destination.longitude,
            active: 1,
            customer_id: customer_id,
            region_id: Destination.region_id,
            updatedat: 'now()',
          },
          on_conflict: {
            constraint: 'locations_customer_id_address_key',
            update_columns: ['active'],
          },
        },
      };
    }
    const nestedLane = Object.assign(Lane, pickupData, deliveryData);
    return nestedLane;
  },

  /**
   * Returns a lane object with maps data, calculated details, and nested Location data
   * @param {Number} customer_id
   * @param {Object} Origin - location containing name, address, and either id or lat and long
   * @param {Object} Destination - location containing name, address, and either id or lat and long
   * @param {Object} mapsData - distance and average speed data returned from Google Maps' distance API
   * @returns {Object} lane object with maps data, calculated details, and nested Location data
   */
  async buildNestedLane(customer_id, Origin, Destination, mapsData) {
    let newLane = {
      active: 1,
      customer_id: customer_id,
      description: `${Origin.name} to ${Destination.name}`,
      distance_miles: mapsData.miles,
      average_drive_speed_mph: mapsData.avgMph,
    };

    const rideshareRatesRes = await _parent.lanes.getRideshareRates(customer_id);
    let rideshareRate = rideshareRatesRes && rideshareRatesRes.data ? rideshareRatesRes.data : null;

    if (log) console.log('assignNestedLocations with: ', newLane, customer_id, Origin, Destination);
    let laneWithNested = lanes.assignNestedLocations(newLane, customer_id, Origin, Destination);
    if (log) console.log('calculateDetails with: ', laneWithNested);
    let newDetailedLane = await lanes.calculateDetails(laneWithNested, rideshareRate);
    if (log) {
      console.log('nested: ' + JSON.stringify(newDetailedLane));
    }
    return laneWithNested;
  },

  //Validate lane params
  validateLaneParams: (customer_id, origin_id, destination_id) => {
    let errorArray = [];
    if (customer_id === undefined) {
      // throw new Error(`customer id is undefined`);
      console.error(new Error('customer id is undefined'));
      errorArray.push({ success: false, errorCode: 'field_invalid', error: new Error('customer id is undefined') });
    }
    if (origin_id === undefined) {
      // throw new Error(`origin id is undefined`);
      console.error(new Error('origin id is undefined'));
      errorArray.push({ success: false, errorCode: 'field_invalid', error: new Error('origin id is undefined') });
    }
    if (destination_id === undefined) {
      // throw new Error(`destination id is undefined`);
      console.error(new Error('destination id is undefined'));
      errorArray.push({ success: false, errorCode: 'field_invalid', error: new Error('destination id is undefined') });
    }
    if (typeof customer_id !== 'number') {
      // throw new Error(`Invalid customer id: customer_id is ${typeof customer_id}`);
      console.error(new Error(`Invalid customer id: customer_id is ${typeof customer_id}`));
      errorArray.push({
        success: false,
        errorCode: 'field_invalid',
        error: new Error(`Invalid customer id: customer_id is ${typeof customer_id}`),
      });
    }
    if (typeof origin_id !== 'number') {
      // throw new Error(`Invalid origin id: origin_id is ${typeof origin_id}`);
      console.error(new Error(`Invalid origin id: customer_id is ${typeof origin_id}`));
      errorArray.push({
        success: false,
        errorCode: 'field_invalid',
        error: new Error(`Invalid origin id: origin_id is ${typeof origin_id}`),
      });
    }
    if (typeof destination_id !== 'number') {
      // throw new Error(`Invalid destination id: destination_id is ${typeof destination_id}`);
      console.error(new Error(`Invalid destination id: destination_id is ${typeof destination_id}`));
      errorArray.push({
        success: false,
        errorCode: 'field_invalid',
        error: new Error(`Invalid destination id: destination_id is ${typeof destination_id}`),
      });
    }
    if (origin_id === destination_id) {
      // throw new Error(`Origin #${origin_id} and Destination #${destination_id} locations cannot be the same`);
      console.error(new Error(`Origin #${origin_id} and Destination #${destination_id} locations cannot be the same`));
      errorArray.push({
        success: false,
        errorCode: 'duplicate_address',
        error: new Error(`Origin #${origin_id} and Destination #${destination_id} locations cannot be the same`),
      });
    }

    if (errorArray.length > 0) {
      return errorArray;
    } else return [{ success: true }];
  },

  /**
   * Enrich a lane and create or enrich its inverse
   *
   * @param {Number} laneId The id of the lane
   * @returns {Obj} Returns a standard custom response object (success, data, errors)
   *
   */
  enrichLaneAndHandleInverse: async function (laneId) {
    try {
      //Declare lane var
      let laneToEnrich = null;

      //Validate lane param
      if (!laneId || typeof laneId !== 'number') throw new Error('Invalid lane id');

      //Get nested lane (we need location addresses to get distance data from Google)
      let getLaneRes = await this.getById(laneId);
      if (!getLaneRes.success) throw new Error('Error fetching lane to enrich');
      else if (getLaneRes.data && getLaneRes.data.length > 0) {
        laneToEnrich = getLaneRes.data[0];
      } else {
        throw new Error(`No lane with id ${laneId} found`);
      }

      //Define location objects (we need id, name and address to build a full lane)
      let pickupLocation = {
        id: _parent.utilities.getPropValue(laneToEnrich, 'pickup.id'),
        name: _parent.utilities.getPropValue(laneToEnrich, 'pickup.name'),
        address: _parent.utilities.getPropValue(laneToEnrich, 'pickup.address'),
      };
      let deliveryLocation = {
        id: _parent.utilities.getPropValue(laneToEnrich, 'delivery.id'),
        name: _parent.utilities.getPropValue(laneToEnrich, 'delivery.name'),
        address: _parent.utilities.getPropValue(laneToEnrich, 'delivery.address'),
      };

      //Get google distance data (we use the same data for each lane)
      let mapsDataObj = await _parent.google.returnLaneData(pickupLocation.address, deliveryLocation.address);
      //Check that google distance data was returned successfully (i.e. values are not 0 or null)
      if (!mapsDataObj || !mapsDataObj.miles || !mapsDataObj.avgMph || !mapsDataObj.duration) {
        console.error('Failed to get distance data from Google.');
        throw new Error('Failed to get distance data from Google.');
      }

      //Build Lane and the inverse
      let primaryLane = new Lane(
        laneToEnrich.customer_id,
        pickupLocation.name,
        pickupLocation.id,
        deliveryLocation.name,
        deliveryLocation.id,
        mapsDataObj
      );
      let inverseLane = new Lane(
        laneToEnrich.customer_id,
        deliveryLocation.name,
        deliveryLocation.id,
        pickupLocation.name,
        pickupLocation.id,
        mapsDataObj
      );

      const rideshareRatesRes = await _parent.lanes.getRideshareRates(laneToEnrich.customer_id);
      let rideshareRate = rideshareRatesRes && rideshareRatesRes.data ? rideshareRatesRes.data : null;

      //Build details for each lane
      let primaryLaneWithDetails = await this.calculateDetails(primaryLane, rideshareRate);
      let inverseLaneWithDetails = await this.calculateDetails(inverseLane, rideshareRate);

      //Check that lanes were detailed correctly.
      //still upsert if distance data has been added

      //Upsert full lanes (we use upsert instead of update in case the inverse lane doesn't exist yet)
      let upsertRes = await this.upsertLanes([primaryLaneWithDetails, inverseLaneWithDetails]);
      return upsertRes;
    } catch (err) {
      //Return standardized response object
      return { success: false, data: [], errors: [err] };
    }
  },

  //NOTE: the above function basically replaces enrichLane. But I already wrote it so I'll keep it for now
  /**
   * Enrich a lane by filling in all missing fields
   *
   * @param {Number} laneId The id of the lane
   * @returns {Obj} Returns a standard custom response object (success, data, errors)
   *
   */
  enrichLane: async function (laneId) {
    try {
      //Declare lane var
      let laneToEnrich = null;

      //Validate lane param
      if (!laneId || typeof laneId !== 'number') throw new Error('Invalid lane id');

      //Get nested lane
      let getLaneRes = await this.getById(laneId);
      if (!getLaneRes.success) throw new Error('Error fetching lane to enrich');
      else if (getLaneRes.data && getLaneRes.data.length > 0) {
        laneToEnrich = getLaneRes.data[0];
      } else {
        throw new Error(`No lane with id ${laneId} found`);
      }

      //Define location objects (we need id, name and address to build a full lane)
      let pickupLocation = {
        id: _parent.utilities.getPropValue(laneToEnrich, 'pickup.id'),
        name: _parent.utilities.getPropValue(laneToEnrich, 'pickup.name'),
        address: _parent.utilities.getPropValue(laneToEnrich, 'pickup.address'),
      };
      let deliveryLocation = {
        id: _parent.utilities.getPropValue(laneToEnrich, 'delivery.id'),
        name: _parent.utilities.getPropValue(laneToEnrich, 'delivery.name'),
        address: _parent.utilities.getPropValue(laneToEnrich, 'delivery.address'),
      };

      //Get google distance data (we use the same data for each lane)
      let mapsDataObj = await _parent.google.returnLaneData(pickupLocation.address, deliveryLocation.address);

      //Build Lane and the inverse
      let laneWithDistance = new Lane(
        laneToEnrich.customer_id,
        pickupLocation.name,
        pickupLocation.id,
        deliveryLocation.name,
        deliveryLocation.id,
        mapsDataObj
      );

      const rideshareRatesRes = await _parent.lanes.getRideshareRates(customer_id);
      let rideshareRate = rideshareRatesRes && rideshareRatesRes.data ? rideshareRatesRes.data : null;
      //Calculate details
      let enrichedLane = await this.calculateDetails(laneWithDistance, rideshareRate);

      //Create object of columns to set
      let updateVars = this.buildLaneUpdateVars(enrichedLane);

      //Update lane
      let laneRes = await this.updateById(laneToEnrich.id, updateVars);
      return laneRes;
    } catch (err) {
      //Return standardized response object
      return { success: false, data: [], errors: [err] };
    }
  },

  /**
   * Get rideshare rates for estimated return ride cost
   * @param {bigint} customer_id - the customer id
   * @returns {Object} - returns object containing rideshare rates
   */

  getRideshareRates: async function (customer_id) {
    try {
      //Fetch customer's raterules
      const GET_RATERULE_GROUPS = gql`
        query getRateRuleGroups($customerId: bigint!) {
          raterulegroups(where: { customer_id: { _eq: $customerId } }) {
            begin_date
            end_date
            ridesharerate {
              initial_cost
              service_fee
              per_mile_rate
              per_minute_rate
              minimum_ride_cost
              maximum_ride_cost
              effective_date
              expiration_date
            }
          }
        }
      `;

      let rideshareRate = null;      
      const rateruleGroupsRes = await _parent.gql.query(GET_RATERULE_GROUPS, { customerId: customer_id });
      if (rateruleGroupsRes && rateruleGroupsRes.data && rateruleGroupsRes.data.length && rateruleGroupsRes.data.length > 0) {
        const rateruleGroups = rateruleGroupsRes.data;
        let mostRecentExpiredRate = null;
        let mostRecentExpiredDate = null;
    
        rateruleGroups.forEach((group) => {
            if (group.begin_date && group.end_date) {
                const beginDate = new Date(group.begin_date);
                const endDate = new Date(group.end_date);
                const currentDate = new Date();
    
                if (beginDate <= currentDate && endDate >= currentDate) {
                    rideshareRate = group.ridesharerate;
                } else if (endDate < currentDate) {
                    if (mostRecentExpiredDate === null || endDate > mostRecentExpiredDate) {
                        mostRecentExpiredDate = endDate;
                        mostRecentExpiredRate = group.ridesharerate;
                    }
                }
            }
        });
    
        if (rideshareRate === null) {
            rideshareRate = mostRecentExpiredRate;
        }
      }
      return {success: true, data: rideshareRate};
    } catch (err) {
      console.log('error in getRideshareRates', err);
      return { success: false, data: null};
    }
  },

  /**
   * Creates an object of fields to update (prevents overwriting ids or description)
   *
   * @param {Object} laneObj - the lane object being updated
   * @returns {Object} - Object with fields to _set
   *
   */
  buildLaneUpdateVars: laneObj => {
    //Create object for "_set" section of update
    return {
      active: 1,
      average_drive_speed_min_per_mile: laneObj.average_drive_speed_min_per_mile,
      average_drive_speed_mph: laneObj.average_drive_speed_mph,
      dealer_base_discount: laneObj.dealer_base_discount,
      dealer_base_price: laneObj.dealer_base_price,
      dealer_base_rate: laneObj.dealer_base_rate,
      dealer_base_rate_type: laneObj.dealer_base_rate_type,
      dealer_stranded_discount: laneObj.dealer_stranded_discount,
      dealer_stranded_price: laneObj.dealer_stranded_price,
      dealer_stranded_rate: laneObj.dealer_stranded_rate,
      dealer_stranded_rate_type: laneObj.dealer_stranded_rate_type,
      delivery_inspection_sec: laneObj.delivery_inspection_sec,
      distance_miles: laneObj.distance_miles,
      driver_base_pay: laneObj.driver_base_pay,
      driver_base_pay_discount: laneObj.driver_base_pay_discount,
      driver_drive_pay: laneObj.driver_drive_pay,
      driver_pay_per_kilometer: laneObj.driver_pay_per_kilometer,
      driver_pay_per_mile: laneObj.driver_pay_per_mile,
      driver_pay_per_minute: laneObj.driver_pay_per_minute,
      driver_rake: laneObj.driver_rake,
      driver_return_pay: laneObj.driver_return_pay,
      driver_return_pay_discount: laneObj.driver_return_pay_discount,
      driver_time_pay: laneObj.driver_time_pay,
      duration_sec: laneObj.duration_sec,
      estimated_rideshare_return_cost: laneObj.estimated_rideshare_return_cost,
      insurance_cost: laneObj.insurance_cost,
      insurance_cost_per_mile: laneObj.insurance_cost_per_mile,
      pickup_inspection_sec: laneObj.pickup_inspection_sec,
      return_ride_wait_sec: laneObj.return_ride_wait_sec,
      tolls: laneObj.tolls || 0,
      updatedat: 'now()',
    };
  },
};

module.exports = lanes;
